import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { ArrowRight, Sparkles } from "lucide-react";
import { OrchestratorTile } from "@/components/brand/OrchestratorLogo";
import HeaderControls from "@/components/HeaderControls";

export default function Login() {
    const { t } = useTranslation();
    const { login } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email, password);
            toast.success(t("auth.welcome_toast"));
            nav("/app/home");
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("auth.login_failed"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Top-right language + theme controls, always visible for public pages */}
            <div className="absolute top-4 right-4 z-10">
                <HeaderControls variant="landing" />
            </div>
            <div className="flex-1 flex items-center justify-center px-6 py-12">
                <form onSubmit={submit} className="w-full max-w-sm jr-fadein">
                    <Link to="/" className="flex items-center gap-2 mb-10">
                        <OrchestratorTile size={32} />
                        <span className="font-display text-2xl tracking-tight">Orchestrator</span>
                    </Link>
                    <h1 className="font-display text-4xl tracking-tight font-semibold">{t("auth.welcome_back")}</h1>
                    <p className="mt-2 text-[var(--jr-text-2)]">{t("auth.login_intro")}</p>

                    <div className="mt-8 space-y-4">
                        <div>
                            <label className="text-xs uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{t("auth.email")}</label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="login-email"
                                className="mt-2 w-full bg-[var(--jr-surface)] border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-[var(--jr-text-3)] focus:border-[var(--jr-brand)] outline-none transition-colors"
                                placeholder={t("auth.email_placeholder")}
                            />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{t("auth.password")}</label>
                            <input
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="login-password"
                                className="mt-2 w-full bg-[var(--jr-surface)] border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-[var(--jr-text-3)] focus:border-[var(--jr-brand)] outline-none transition-colors"
                                placeholder={t("auth.password_placeholder")}
                            />
                        </div>
                    </div>

                    <button type="submit" disabled={loading} data-testid="login-submit" className="mt-8 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[var(--jr-brand)] text-white font-medium hover:bg-[var(--jr-brand-2)] transition-colors disabled:opacity-60">
                        {loading ? t("auth.logging_in") : t("auth.sign_in")} <ArrowRight size={16}/>
                    </button>

                    <p className="mt-6 text-sm text-[var(--jr-text-2)]">
                        {t("auth.no_workspace_yet")}{" "}
                        <Link to="/register" className="text-[var(--jr-brand)] hover:text-[var(--jr-brand-2)]" data-testid="goto-register">{t("auth.create_in_60")}</Link>
                    </p>

                    <div className="mt-10 text-xs text-[var(--jr-text-3)] font-mono">
                        {t("auth.demo_creds")}: <span className="text-[var(--jr-text-2)]">demo@jeremai.io</span> · <span className="text-[var(--jr-text-2)]">Demo12345</span>
                    </div>
                </form>
            </div>
            <div className="hidden lg:block flex-1 relative border-l border-white/8 bg-[var(--jr-bg-2)] overflow-hidden">
                <div aria-hidden className="absolute inset-0 bg-dotgrid opacity-50"/>
                <div aria-hidden className="absolute top-1/3 -right-32 w-[500px] h-[500px] rounded-full bg-[var(--jr-brand)]/10 blur-[120px]"/>
                <div className="relative h-full flex items-center justify-center px-12">
                    <div className="max-w-md">
                        <Sparkles className="text-[var(--jr-brand)]" size={32}/>
                        <p className="mt-6 font-display text-3xl tracking-tight leading-tight">
                            {t("auth.quote")}
                        </p>
                        <p className="mt-6 text-[var(--jr-text-2)] text-sm font-mono uppercase tracking-[0.16em]">
                            {t("auth.quote_author")}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
